import React, { useState } from "react";
import { Button, Grid, Input, Table } from "semantic-ui-react";
import { useSelector } from "react-redux";
import _ from "lodash";
import { searchFilter } from "../../../app/common/util/util";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import EmailsList from "../../emails/emailComponents/EmailList";
import HistoryListItem from "../../history/historyComponents/HistoryListItem";
import useFirestoreCollectionNoAsync from "../../../app/hooks/useFirestoreCollectionNoAsync";
import {
  fetchPartyEmailHistoryFromDb,
  fetchPartyHistoryFromDb,
} from "../../../app/firestore/firestoreService";

// Shows what this party (agent/coagent/TC sharing the same party portal link)
// has shared or sent for signing to their own clients, plus the emails that
// went out for it. Both are restricted to this party's own transactionId +
// id, so the other side of the transaction never sees them — see
// restrictedToPartyId in firestoreService.js.
export default function PartyEmailHistory() {
  const { party } = useSelector((state) => state.party);
  const { transaction } = useSelector((state) => state.transaction);
  const [emails, setEmails] = useState(null);
  const [history, setHistory] = useState(null);
  const [searchTerms, setSearchTerms] = useState("");
  const [activeView, setActiveView] = useState("Documents Shared");

  // On a hard refresh, party/transaction start out as {} (redux initial state)
  // and are only populated once PartyData's async fetches resolve. Without
  // retainState here, the effect would fire immediately with undefined ids and
  // crash — Firestore's where() throws on an undefined field value.
  const notReady = !transaction?.id || !party?.id;

  useFirestoreCollectionNoAsync({
    query: () => fetchPartyEmailHistoryFromDb(transaction.id, party.id),
    data: (emailsFromDb) =>
      setEmails(_.orderBy(emailsFromDb, "createdAt", "desc")),
    deps: [transaction?.id, party?.id],
    retainState: notReady,
  });

  useFirestoreCollectionNoAsync({
    query: () => fetchPartyHistoryFromDb(transaction.id, party.id),
    data: (historyFromDb) =>
      setHistory(_.orderBy(historyFromDb, "createdAt", "desc")),
    deps: [transaction?.id, party?.id],
    retainState: notReady,
  });

  if (notReady) {
    return <LoadingComponent content="Loading history..." />;
  }

  const filteredEmails = searchFilter(emails || [], searchTerms);
  const filteredHistory = searchFilter(history || [], searchTerms);

  return (
    <div className="secondary-page-wrapper">
      <Grid stackable>
        <Grid.Column computer={16}>
          <h3>History</h3>
        </Grid.Column>
        <Grid.Column computer={6}>
          <Button.Group fluid size="small">
            <Button
              active={activeView === "Documents Shared"}
              onClick={() => setActiveView("Documents Shared")}
            >
              Documents Shared
            </Button>
            <Button
              active={activeView === "Emails Sent"}
              onClick={() => setActiveView("Emails Sent")}
            >
              Emails Sent
            </Button>
          </Button.Group>
        </Grid.Column>
        <Grid.Column computer={10}>
          <Input
            type="text"
            fluid
            placeholder="Search by name, email, action, document, or text in a message"
            value={searchTerms}
            onChange={(e) => setSearchTerms(e.target.value)}
          ></Input>
        </Grid.Column>

        <Grid.Column computer={16}>
          {activeView === "Documents Shared" ? (
            history === null ? (
              <LoadingComponent content="Loading history..." />
            ) : filteredHistory.length > 0 ? (
              <Table compact className="mini top margin">
                <Table.Header className="mobile hidden">
                  <Table.Row className="small-header">
                    <Table.HeaderCell>Date</Table.HeaderCell>
                    <Table.HeaderCell>Role</Table.HeaderCell>
                    <Table.HeaderCell>Name</Table.HeaderCell>
                    <Table.HeaderCell>Action</Table.HeaderCell>
                    <Table.HeaderCell>Document</Table.HeaderCell>
                    <Table.HeaderCell>To Role</Table.HeaderCell>
                    <Table.HeaderCell>To Name</Table.HeaderCell>
                    <Table.HeaderCell>To Email</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredHistory.map((entry) => (
                    <HistoryListItem entry={entry} key={entry.id} />
                  ))}
                </Table.Body>
              </Table>
            ) : (
              <p>You haven't shared or sent anything to your clients yet.</p>
            )
          ) : emails === null ? (
            <LoadingComponent content="Loading history..." />
          ) : filteredEmails.length > 0 ? (
            <EmailsList emails={filteredEmails} />
          ) : (
            <p>You haven't sent any emails to your clients yet.</p>
          )}
        </Grid.Column>
      </Grid>
    </div>
  );
}
